import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { Trackable, TrackableType } from "@redotech/redo-model/trackable";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import MarkerPin from "@redotech/redo-web/arbiter-icon/marker-pin-01.svg";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import { InteractiveMap } from "./in-store-locations";
import * as returnLocationsStyles from "./return-locations-button.module.css";

const supportedCarrierLocations = [
  "USPS",
  "USPSReturns",
  "UspsShip",
  "FedEx",
  "FedExSmartPost",
];

export const ReturnLocationsButton = memo(function ReturnLocationsButton({
  trackable,
}: {
  trackable: Trackable & { type: TrackableType.RETURN };
}) {
  const rpcClient = useRequiredContext(RpcClientContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [zip, setZip] = useState(trackable?.shipping_address?.zip || "");
  const [locationsReloadCount, setLocationsReloadCount] = useState(0);

  const carrier: string | undefined =
    trackable?.shipment?._shipment?.selected_rate?.carrier;

  const country = trackable.shipping_address?.country || "US";

  const [locationsLoad, getLocations] = useTriggerLoad(async () => {
    if (!carrier || !zip) return null;
    const result = await rpcClient.getCarrierLocations({
      carrier,
      zip,
      country,
    });
    setLocationsReloadCount((count) => count + 1);
    return result;
  });

  useEffect(() => {
    if (carrier && modalOpen && !locationsLoad.value) {
      getLocations();
    }
  }, [carrier, modalOpen, getLocations, locationsLoad.value]);

  if (!carrier || !supportedCarrierLocations.includes(carrier)) {
    return null;
  }

  return (
    <>
      <Flex pt="xs">
        <RedoButton
          hierarchy="secondary"
          IconLeading={MarkerPin}
          onClick={() => setModalOpen(true)}
          text="Drop off locations"
        />
      </Flex>
      <RedoModal
        className={returnLocationsStyles.locationsModal}
        isOpen={modalOpen}
        onModalCloseRequested={() => setModalOpen(false)}
        title="Drop off locations"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            getLocations();
          }}
        >
          <Flex gap="sm" m="lg">
            <RedoTextInput
              fullWidth
              placeholder="Zip code"
              setValue={setZip}
              value={zip}
            />
            <RedoButton hierarchy="secondary" text="Search" type="submit" />
          </Flex>
        </form>
        {locationsLoad.pending ? (
          <LoadingRedoAnimation />
        ) : locationsLoad.error ? (
          <Text textColor="error">Error loading locations</Text>
        ) : locationsLoad.value?.locations ? (
          <InteractiveMap
            key={locationsReloadCount}
            locations={locationsLoad.value.locations}
          />
        ) : null}
      </RedoModal>
    </>
  );
});
